import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import l10n from '../../lib/reactPPl10nHelper';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import Page from '../../lib/page';
import poll from '../../lib/poll';
import fireImpressionFptiForChatLoadError from '../../lib/fire-impression-fpti-for-chat-load-error';
import { nodeChatOnPageValidator } from '../../lib/node-chat-on-page-validator';
import Mutation from '../../lib/mutation-observer';
import AsyncHelper from '../../lib/asyncMessageHelper';
import { unreadConversationChecker, getAsyncLastActiveConversation } from '../../lib/conversationMsgHelper';

const question = l10n.getContent('smarthelp/chatBot.message.question');
const bubbleText = l10n.getContent('smarthelp/chatBot.bubble.text');
const btnText = l10n.getContent('smarthelp/chatBot.button.text');
const rejectText = l10n.getContent('smarthelp/chatBot.bubble.reject');
const asyncPromptText = l10n.getContent('smarthelp/chatBot.asyncPrompt.question');
const asyncBubbleText = l10n.getContent('smarthelp/chatBot.asyncBubble.text');
const isLoggedIn = Page.isLoggedIn();

// the Poll function will retry for at least 3.20 secs based on current retries = 5 and delay = 200 ms
const pollRetries = 5;
const pollDelay = 200;

class ChatBot extends Component {
  static getPromptMessage() {
    if (isLoggedIn && Page.isPilotUser()) {
      return asyncPromptText;
    }
    return question;
  }

  static getBubbleText() {
    if (isLoggedIn && Page.isPilotUser()) {
      return asyncBubbleText;
    }
    return btnText;
  }

  constructor(props) {
    super(props);
    this.nodeChatElement = null;
    this.getnodeChatElement = this.getnodeChatElement.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    if (this.props.display) {
      this.chatBotTracking();
      if (isLoggedIn) Mutation.waitForBodyToLoad(Mutation.nodeChatObserver);
    }
  }

  componentWillUpdate(nextProps) {
    if (this.props.display === false && nextProps.display === true) {
      this.chatBotTracking();
      if (isLoggedIn) Mutation.waitForBodyToLoad(Mutation.nodeChatObserver);
    }
  }

  componentDidUpdate() {
    this.hidePrompt();
  }

  getnodeChatElement(id) {
    this.nodeChatElement = this.nodeChatElement || document.getElementById(id);
    return this.nodeChatElement;
  }

  hidePrompt() { // eslint-disable-line class-methods-use-this
    if (isLoggedIn) {
      const target = this.getnodeChatElement('node-chat');
      if (target && target.style.display && target.style.display === 'block') {
        const chatNowBtn = document.getElementById('chat-now-btn');
        if (chatNowBtn) {
          chatNowBtn.style.display = 'none';
        }
      }
    }
  }

  chatBotTracking() {
    // Add expection for sending impression event on the final intent (L2, L3) topic tree in channel steering
    if (Page.isContactUsPage() && typeof this.props.topicRefId === 'string' && this.props.topicRefId !== 'GENPOOL|contactUsPage') {
      return Promise.resolve();
    }

    const validateFpti = () => {
      if (!window.fpti) {
        return Promise.reject(false);
      }
      return Promise.resolve(window.fpti);
    };
    return poll(validateFpti, 4, 500)
      .then(() => trackPage({ link: this.props.link, id: this.props.id, hpid: this.props.hpid }))
      .catch(() => {});
  }

  sessionChecker() { // eslint-disable-line class-methods-use-this
    const chatCardClicked = sessionStorage && sessionStorage.getItem('chatCardClicked') !== 'true';
    return chatCardClicked;
  }

  handleClose(e) { // eslint-disable-line class-methods-use-this
    const chatPromptCard = document.getElementById('chat-question-card');
    chatPromptCard.classList.add('fade-out');
    const chatNowBtn = document.getElementById('chat-now-btn');
    chatNowBtn.style.visibility = 'visible';
    chatNowBtn.style.position = 'relative';
    chatNowBtn.classList.add('btn-fadein');
    sessionStorage.setItem('chatCardClicked', 'true');
    chatPromptCard.style.position = 'absolute';
    chatPromptCard.style.left = '-99999px';
    e.cancelBubble = true; // otherwise, close will bubble up and invoke chat bot.
    if (e.stopPropagation) e.stopPropagation();
  }

  chatBotPrompt() {
    return (<div // eslint-disable-line jsx-a11y/no-static-element-interactions
      id="chat-question-card" className="chat-question-card fade-in"
    >
      <button onClick={this.handleClose} className="close-btn" />
      <p className="ppl-logo" />
      <p className="chatbot-question">{ChatBot.getPromptMessage()}</p>
      <p className="chatbot-text">{bubbleText}</p>
      <button onClick={_debounce(this.handleClick, 2000)} className="chat-question-card-btn"><span className="chat-flex"><span className="chat-icon" aria-hidden="true" />
        <p className="btn-text">{ChatBot.getBubbleText()}</p></span></button>
      <button onClick={this.handleClose} className="reject-text">{rejectText}</button>
    </div>);
  }

  handleClick() {
    this.props.chatBotClicked();
    const chatbotClicked = sessionStorage && sessionStorage.getItem('chatBotClicked') === 'true';
    // Making chatcardclicked true when user clicks on chatbot in both logout & login
    sessionStorage.setItem('chatCardClicked', 'true');
    const liveChatClicked = sessionStorage && sessionStorage.getItem('liveChatClicked') === 'true';
    if (!chatbotClicked && !isLoggedIn) {
      sessionStorage.setItem('chatBotClicked', 'true');
    } else if (!liveChatClicked && isLoggedIn) {
      // when user first clicked on chatbot and then clicked on the chat need to open the live-chat
      sessionStorage.setItem('liveChatClicked', 'true');
    }
    trackLink({ link: `${this.props.link}|clicked`, id: this.props.id, hpid: this.props.hpid });
    if (!isLoggedIn) {
      return null;
    }

    // Login Pilot User checking
    if (Page.isPilotUser()) {
      // If new New MMT async conversations API is enabled.
      if (Page.isMMTAsyncLastActiveServiceEnabled()) {
        return AsyncHelper.redirectPromiseAfterAsyncConversationCheck(getAsyncLastActiveConversation, { topicRefId: this.props.topicRefId });
      }

      // Fallback to old Async scenario
      return AsyncHelper.rediretPromise(unreadConversationChecker, { topicRefId: this.props.topicRefId });
    }

    // Waits at least 3.20 secs based on current retries = 5 and delay = 200 ms
    return poll(nodeChatOnPageValidator, pollRetries, pollDelay)
      .then(() => {
        // eslint-disable-next-line no-undef
        nodeChat.show('chatBot', this.props.topicRefId);
      })
      .catch(() => fireImpressionFptiForChatLoadError(this.props.link, this.props.hpid));
  }

  hideChatBot() {
    return !this.props.display || Page.disableChatbotInMobileTablet() || Page.isLexaPilotFailure();
  }

  render() {
    if (this.hideChatBot()) {
      return null;
    }
    const chatBtnClassName = this.sessionChecker() ? 'chat-btn' : 'chat-btn btn-fadein';
    return (
      <div id="smarthelp-chatBot-prompt" className="chatBot-prompt">
        {this.sessionChecker() && this.chatBotPrompt()}
        <button
          style={{ position: this.sessionChecker() ? 'absolute' : 'relative' }}
          id="chat-now-btn" onClick={_debounce(this.handleClick, 2000)} className={chatBtnClassName}
        >
          <span className="chat-flex"><span className="chat-icon" aria-hidden="true" /><p className="btn-text">{ChatBot.getBubbleText()}</p></span></button>
      </div>
    );
  }
}

ChatBot.propTypes = {
  id: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  hpid: PropTypes.string.isRequired,
  display: PropTypes.bool.isRequired,
  chatBotClicked: PropTypes.func,
  topicRefId: PropTypes.string,
};

ChatBot.defaultProps = {
  id: '',
  link: '',
  hpid: '',
  display: false,
  topicRefId: 'GENPOOL', // GENPOOL is generic topic id
  chatBotClicked: () => {},
};

export default ChatBot;
